import React from "react";
import { Form, Upload, Button } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd";
import { useTranslation } from "react-i18next";
import { srmUploadService } from "@/utils/upload";
import { notifyError, notifySuccess } from "@/utils/notification";
import { FileListItem } from "@/components/FileListItem/FileListItem";
import { uploadSupplierFlowBusinessFiles } from "@/apis/flow.api";

// ==================== 类型定义 ====================

interface FileFormFieldProps {
  fieldName: string;
  label?: string;
  initialFiles?: Array<{ name: string; url: string }>;
  required?: boolean;
  readOnly?: boolean;
  fileType?: number; // 文件类型编码
}
/**
 * 文件上传配置（基础配置，不包含customRequest）
 */
const uploadProps: UploadProps = {
  name: "file",
  showUploadList: false,
  accept: "image/*,.pdf",
  multiple: true,
  maxCount: 5,
};

/**
 * 文件上传变化处理函数
 * 处理文件上传的状态更新
 */
const onFileChange =
  (fieldName: string, form: any, allFilesRef: any, t: any) => (info: any) => {
    // 获取当前表单中已有的文件列表（从 ref 中获取，而不是从 form）
    const currentFiles = allFilesRef.current || [];

    // 获取当前上传的文件列表
    const uploadingFiles = Array.isArray(info.fileList) ? info.fileList : [];

    // 创建一个 uid 到文件的映射，用于快速查找和更新
    const fileMap = new Map<string, any>();

    // 先添加已有的文件
    currentFiles.forEach((file: any) => {
      fileMap.set(file.uid, file);
    });

    // 然后用新的上传文件更新或添加
    uploadingFiles.forEach((file: any) => {
      const existingFile = fileMap.get(file.uid);

      // 优先使用 response 中的 fileId，其次使用现有的 fileId
      const fileId = file.response?.fileId || existingFile?.fileId;

      fileMap.set(file.uid, {
        ...existingFile, // 保留原有属性
        ...file, // 用新属性覆盖
        url: file.response?.url || file.url || existingFile?.url, // 保留已有的 URL
        fileId: fileId, // 确保 fileId 被保留（不被覆盖）
        response: file.response || existingFile?.response, // 保留完整的 response 对象
      });
    });

    // 转换为数组
    const mergedFiles = Array.from(fileMap.values());

    // 更新 ref，保存所有文件
    allFilesRef.current = mergedFiles;

    form.setFieldsValue({
      [fieldName]: mergedFiles,
    });

    const errorFiles = uploadingFiles.filter((f: any) => f.status === "error");
    if (errorFiles.length > 0) {
      errorFiles.forEach((errorFile: any) => {
        notifyError(`${errorFile.name} ${t("文件上传失败")}`);
      });
    }
  };

// ==================== 主组件 ====================

const FileFormField: React.FC<FileFormFieldProps> = ({
  fieldName,
  label,
  initialFiles = [],
  required = true,
  readOnly = false,
  fileType = 5, // 默认为风险等级文件类型
}) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const uploadQueueRef = React.useRef<
    Array<{ url: string; fileName: string; onSuccess: Function }>
  >([]);
  const totalFilesCountRef = React.useRef<number>(0);
  const completedFilesCountRef = React.useRef<number>(0);
  // 使用 ref 来保存所有文件，确保在 onChange 中能获取到之前的文件
  const allFilesRef = React.useRef<any[]>([]);

  /**
   * 初始化表单中的文件字段（当组件挂载或 initialFiles 变化时）
   */
  React.useEffect(() => {
    if (form && initialFiles && initialFiles.length > 0) {
      // 转换 initialFiles 为 Upload 组件需要的格式
      const uploadFiles: UploadFile[] = initialFiles.map((file: any) => ({
        uid: file.uid || file.url || `file-${Math.random()}`,
        name:
          file.name || file.fileName || file.url?.split("/").pop() || "文件",
        status: "done" as const,
        url: file.url,
        fileId: file.fileId || file.id,
        originFileObj: undefined,
      }));

      // 同时更新 ref 和表单字段值
      allFilesRef.current = uploadFiles;
      form.setFieldsValue({
        [fieldName]: uploadFiles,
      });
    }
  }, [form, fieldName, initialFiles]);

  /**
   * 批量调用API上传文件信息（所有文件在一个请求中）
   */
  const batchUploadFilesToAPI = React.useCallback(
    async (
      filesToUpload: Array<{
        url: string;
        fileName: string;
        onSuccess: Function;
      }>,
    ) => {
      if (filesToUpload.length === 0) return;

      try {
        // 准备批量上传的数据
        const fileDataList = filesToUpload.map((file) => ({
          fileUrl: file.url,
          fileName: file.fileName,
          fileType,
        }));

        // 一次API调用，传所有文件数据
        const response = await uploadSupplierFlowBusinessFiles(fileDataList);

        // 处理响应：返回的可能是数组或单个对象
        const fileIds = Array.isArray(response?.data?.data)
          ? response?.data?.data
          : response?.data?.data
            ? [response?.data?.data]
            : [];

        console.log(`[批量上传成功] 返回${fileIds.length}个fileId:`, fileIds);

        // 为每个文件调用对应的onSuccess，传递fileId
        filesToUpload.forEach((file, index) => {
          const fileId = fileIds[index];
          file.onSuccess?.({ url: file.url, fileId });
        });

        notifySuccess(`成功批量上传${filesToUpload.length}个文件`);
      } catch (apiError: any) {
        console.error("[批量上传失败]", apiError);
        // API调用失败时，仍然为所有文件调用onSuccess，因为文件已上传到OSS
        filesToUpload.forEach((file) => {
          file.onSuccess?.({ url: file.url });
        });
        notifyError("文件信息记录失败，但文件已成功上传到OSS");
      }
    },
    [fileType],
  );

  /**
   * 检查是否所有文件都已准备好，如果是则执行批量上传
   */
  const checkAndExecuteBatchUpload = React.useCallback(() => {
    completedFilesCountRef.current++;

    if (
      completedFilesCountRef.current >= totalFilesCountRef.current &&
      uploadQueueRef.current.length > 0
    ) {
      const filesToUpload = uploadQueueRef.current;
      uploadQueueRef.current = [];
      totalFilesCountRef.current = 0;
      completedFilesCountRef.current = 0;

      batchUploadFilesToAPI(filesToUpload);
    }
  }, [batchUploadFilesToAPI]);

  // ==================== 文件上传处理 ====================
  const customRequest = async ({ file, onSuccess, onError }: any) => {
    try {
      const fileName = (file as File).name;
      totalFilesCountRef.current++;

      // 上传文件到OSS
      const url = await srmUploadService.uploadFile(file as File);

      console.log("单个文件OSS上传成功:", { fileName, url });

      // 将文件加入批量上传队列
      uploadQueueRef.current.push({
        url,
        fileName,
        onSuccess: (response: any) => {
          console.log("🔥 文件onSuccess被调用，response:", response);
          console.log(
            "🔥 当前表单中的文件列表:",
            form.getFieldValue(fieldName),
          );

          // 关键：立即更新表单中的文件对象，确保fileId被保存
          // 优先从 allFilesRef 获取，确保能获取到所有文件
          const currentFiles =
            allFilesRef.current && Array.isArray(allFilesRef.current)
              ? allFilesRef.current
              : Array.isArray(form.getFieldValue(fieldName))
                ? form.getFieldValue(fieldName)
                : [];

          const updatedFiles = currentFiles.map((f: any) => {
            // 通过文件名匹配找到对应的文件，更新其fileId
            // 优先使用 URL 匹配（更精确），如果 URL 不匹配则使用文件名匹配（更可靠）
            const isUrlMatch = f.url === response.url;
            const isNameMatch =
              f.name === response.fileName || f.name === fileName;

            if (isUrlMatch || isNameMatch) {
              console.log(
                `🔥 匹配到文件 ${f.name}，更新fileId: ${response.fileId} (URL匹配: ${isUrlMatch}, 名称匹配: ${isNameMatch})`,
              );
              return {
                ...f,
                url: response.url || f.url, // 确保 URL 被更新
                fileId: response.fileId,
                response: response,
              };
            }
            return f;
          });

          console.log("🔥 更新后的文件列表:", updatedFiles);

          // 同时更新 ref 和表单
          allFilesRef.current = updatedFiles;
          form.setFieldsValue({
            [fieldName]: updatedFiles,
          });

          onSuccess?.(response);
        },
      });

      checkAndExecuteBatchUpload();
    } catch (err: any) {
      console.error("文件上传失败:", err);
      completedFilesCountRef.current++;
      checkAndExecuteBatchUpload();
      onError?.(err);
    }
  };

  return (
    <div>
      {!readOnly && (
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <Form.Item
            name={fieldName}
            valuePropName="fileList"
            style={{ margin: 0 }}
          >
            <Upload
              {...uploadProps}
              customRequest={customRequest}
              onChange={onFileChange(fieldName, form, allFilesRef, t)}
            >
              <Button
                icon={<UploadOutlined />}
                variant="outlined"
                color="primary"
              >
                {t("点击上传")}
              </Button>
            </Upload>
          </Form.Item>
        </div>
      )}

      {/* 自定义文件列表：预览 + 下载 + 删除 */}
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue, setFieldsValue }) => {
          const fileList = form.getFieldValue(fieldName);
          if (!fileList || !Array.isArray(fileList)) return null;

          const removeFile = (url?: string) => {
            if (readOnly) return;
            setFieldsValue({
              [fieldName]: fileList.filter((p: any) => p.url !== url),
            });
          };

          return (
            <div style={{ marginBottom: "12px" }}>
              {fileList.map((file: UploadFile, index: number) => (
                <FileListItem
                  key={`${fieldName}-${file.uid || file.url || ""}-${index}`}
                  file={file}
                  hiddenRemove={readOnly}
                  remove={removeFile}
                />
              ))}
            </div>
          );
        }}
      </Form.Item>
    </div>
  );
};

export default FileFormField;
